import { faFileCsv, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AddIcon from '@mui/icons-material/Add';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LinkIcon from '@mui/icons-material/Link';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import {
    Box,
    Button,
    Card,
    CardContent,
    DialogContent,
    Grid,
    IconButton,
    Stack,
    Theme,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { blue, green, orange, purple } from '@mui/material/colors';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro';
import { DateRange } from '@mui/x-date-pickers-pro';
import dinero from 'dinero.js';
import { parse as parseCsv } from 'json2csv';
import moment, { Moment } from 'moment';
import { useDialog } from 'muibox';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Link, useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import useAxios from '../../../../../../hooks/useAxios';
import useSextforceMetricsCampaignsClaimedTodayOverview from '../../../../../../hooks/useSextforceMetricsCampaignsClaimedToday';
import useSextforceMetricsCampaignsOverview, { EnrichedCampaign } from '../../../../../../hooks/useSextforceMetricsCampaignsOverview';
import { fetchMetricCrossReferenceEarnings } from '../../../../../../hooks/useSextforceMetricsCrossReferenceMetricEarnings';
import useSubscribers from '../../../../../../hooks/useSubscribers';
import { SettingsContext } from '../../../../../../store/SettingsContext';
import { UserContext } from '../../../../../../store/UserContext';
import { OnlyFansLists } from '../../../../../../types/onlyFansTypes';
import { d2f, handleHttpError, metricTypeName } from '../../../../../../utils/common';
import DialogAdaptive from '../../../../../common/DialogAdaptive';
import OverviewStat from '../../../../../common/OverviewStat';
import StyledCardTitleBar from '../../../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../../../common/StyledDialogActions';
import SextforceMetricsCampaignsCreateCampaignDialog from '../SextforceMetricsCampaignsCreateCampaignDialog';
import SextforceMetricsCampaignsClaimsTodayOverview from './SextforceMetricsCampaignsClaimsTodayOverview';
import SextforceMetricsCampaignsOverviewExportProgressDialog from './SextforceMetricsCampaignsOverviewExportProgressDialog';
import SextforceMetricsCampaignsOverviewGrid from './SextforceMetricsCampaignsOverviewGrid';
import SextforceMetricsCampaignsOverviewViewSettingsDialog from './SextforceMetricsCampaignsOverviewViewSettingsDialog';

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const crossReferenceSubscriberIdStorageKey = (subscriberId: string) =>
    `sextforceMetricsCampaignsOverviewCrossReferenceSubscriberId_${subscriberId}`;

/**
 * Transforms the transactions data rows from the server to a format ready for export to CSV or Excel
 * @param rows Transactions rows
 * @param timezone Timezone string to convert the transaction time to
 * @returns Transformed data for export
 */
const transformTransactionsForExport = (rows: EnrichedCampaign[], amountType: 'gross' | 'net', timezone: string) => {
    const headers = [
        {
            label: 'ID',
            value: 'foreignId',
        },
        {
            label: 'Type',
            value: 'type',
        },
        {
            label: 'Active',
            value: 'active',
        },
        {
            label: `Created At (${timezone})`,
            value: 'createdAt',
        },
        {
            label: 'OnlyFans Name',
            value: 'name',
        },
        {
            label: 'Platform',
            value: 'platform',
        },
        {
            label: '@ Username',
            value: 'associatedUsername',
        },
        {
            label: 'Clicks Total',
            value: 'clicksTotal',
        },
        {
            label: 'Claims Today',
            value: 'claimedToday',
        },
        {
            label: 'Claims Total',
            value: 'claimsCount',
        },
        {
            label: 'Claims Goal',
            value: 'goalClaims',
        },
        {
            label: 'Cost Per Fan Net',
            value: 'cpf',
        },
        {
            label: `Earnings ${amountType === 'gross' ? 'Gross' : 'Net'} Total (USD)`,
            value: 'earningsTotal',
        },
        {
            label: `Earning ${amountType === 'gross' ? 'Gross' : 'Net'} Goal (USD)`,
            value: 'goalSpent',
        },
        {
            label: `Earnings ${amountType === 'gross' ? 'Gross' : 'Net'} from Cross Reference Subscriber (USD)`,
            value: 'crossReferenceEarnings',
        },
        {
            label: `Total Earnings with Cross Reference Subscriber ${amountType === 'gross' ? 'Gross' : 'Net'} (USD)`,
            value: 'totalWithCrossReferenceEarnings',
        },
    ];

    const data = rows.map(row => {
        const earningsTotal =
            row.payload && row.payload.earningsTotal && typeof row.payload.earningsTotal === 'object'
                ? d2f(row.payload.earningsTotal) * (amountType === 'gross' ? 1 : 0.8)
                : 0;
        const claimsCount = (row.payload && row.payload.counters && row.payload.counters.countSubscribers) || 0;
        const clicksTotal = (row.payload && row.payload.counters && row.payload.counters.countTransitions) || 0;
        const cpf = row.payload && row.payload.cpf ? Math.round(row.payload.cpf * 100) / 100 : 0;
        const recentEarnings = typeof row.recentEarnings === 'object' ? d2f(row.recentEarnings) * (amountType === 'gross' ? 1 : 0.8) : 0;
        const crossReferenceEarnings = row.crossReferenceEarnings
            ? d2f(amountType === 'gross' ? row.crossReferenceEarnings.total : row.crossReferenceEarnings.totalNet)
            : 0;

        return {
            foreignId: row.foreignId,
            type: metricTypeName(row.type),
            active: row.payload?.active || false,
            createdAt: (row.payload && moment(row.payload.createdAt).tz(timezone).format('L hh:mm a')) || '',
            name: (row.payload && row.payload.name) || '',
            platform: (row.settings && row.settings.platform) || '',
            associatedUsername: row.associatedUsername || '',
            clicksTotal,
            claimedToday: row.subscribedToday || 0,
            claimsCount,
            goalClaims: (row.settings && row.settings.goalSubs) || 0,
            recentEarnings,
            cpf,
            earningsTotal,
            goalSpent: (row.settings && row.settings.goalSpent && d2f(row.settings.goalSpent) * (amountType === 'gross' ? 1 : 0.8)) || 0,
            crossReferenceEarnings,
            totalWithCrossReferenceEarnings: earningsTotal + crossReferenceEarnings,
        };
    });

    return { headers, rows: data };
};

type Props = {
    subscriber: any | null;
    timezone: string;
    setTimezone: (timezone: string) => void;
    theme: Theme;
};

const SextforceMetricsCampaignsOverview = (props: Props) => {
    const { subscriber, timezone, setTimezone, theme } = props;

    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const params = useParams();
    const dialog = useDialog();
    const queryClient = useQueryClient();
    const axios = useAxios();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('sm'));

    const [showInstructions, setShowInstructions] = useState<boolean>(false);
    const [isSavingSettings, setIsSavingSettings] = useState<boolean>(false);
    const [amountType, setAmountType] = useState<'gross' | 'net'>(localStorage.getItem('showEarningsAsGross') === 'true' ? 'gross' : 'net');

    // Campaigns Overview
    const [createCampaignDialogOpen, setCreateCampaignDialogOpen] = useState<boolean>(false);
    const [claimedTodayDate, setClaimedTodayDate] = useState<Date | null>(moment().startOf('day').add(1, 'hour').toDate());

    // Settings
    const [settingsDialogOpen, setSettingsDialogOpen] = useState<boolean>(false);

    const [trialsSelectorType, setTrialsSelectorType] = useState<{ includeInactive: boolean }>({
        includeInactive: localStorage.getItem('sextforceMetricsCampaignsOverviewSelectorType') === 'true',
    });
    const [completedType, setCompletedType] = useState<boolean | null>(
        localStorage.getItem('sextforceMetricsOverviewCompletedType') === 'true'
            ? true
            : localStorage.getItem('sextforceMetricsOverviewCompletedType') === 'false'
            ? false
            : null,
    );
    const [reportSort, setReportSort] = useState<GridSortModel>([
        {
            field: 'payload.createdAt',
            sort: 'desc',
        },
    ]);
    // const [filter, setFilter] = useState<GridFilterModel>({ items: [] });
    const [search, setSearch] = useState<string>('');
    const [searchDateRange, setSearchDateRange] = useState<DateRange<Moment>>([null, null]);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        pageSize: 20,
        page: 0,
    });

    const {
        data: campaigns,
        isLoading: campaignslsLoading,
        refetch: campaignsRefetch,
    } = useSextforceMetricsCampaignsOverview(
        subscriber,
        timezone,
        searchDateRange,
        search,
        paginationModel.page,
        paginationModel.pageSize,
        reportSort && reportSort.length > 0 ? `${reportSort[0].field}=${reportSort[0].sort}` : '',
        trialsSelectorType.includeInactive,
        completedType,
    );

    // Totals for the overview stats cards
    const earningsTotal: number = campaigns?.metadata?.earningsTotal
        ? typeof campaigns.metadata.earningsTotal === 'number'
            ? campaigns.metadata.earningsTotal
            : d2f(campaigns.metadata.earningsTotal)
        : 0;
    const subscribersTotal: number = campaigns?.metadata?.subscribersTotal || 0;
    const clicksTotal: number = campaigns?.metadata?.clicksTotal || 0;

    // Download
    const [campaingsFullForDownloadLoading, setCampaignsFullForDownloadLoading] = useState<boolean>(false);
    const [exportDialogOpen, setExportDialogOpen] = useState<boolean>(false);
    const [exportProgress, setExportProgress] = useState({ message: '', fetched: 0, total: 0 });
    const exportAbortControllerRef = useRef<AbortController | null>(null);
    const exportCancelledRef = useRef(false);

    // Cross Reference Subscriber
    const [crossReferenceSubscriberId, setCrossReferenceSubscriberId] = useState<string>('none');

    const updateCrossReferenceSubscriberId = useCallback(
        (id: string) => {
            setCrossReferenceSubscriberId(id);

            if (subscriber?._id) {
                localStorage.setItem(crossReferenceSubscriberIdStorageKey(subscriber._id), id);
            }
        },
        [subscriber?._id],
    );

    // Subscribers
    const { data: subscribers, isLoading: subscribersLoading } = useSubscribers();

    // Claims Overview
    const [overviewDays, setOverviewDays] = useState<number>(
        localStorage.getItem('sextforceMetricsClaimsTodayOverviewOverviewDays')
            ? parseInt(localStorage.getItem('sextforceMetricsClaimsTodayOverviewOverviewDays') as string)
            : 7,
    );

    const { data: claimsTodayOverview, isLoading: claimsTodayOverviewLoading } = useSextforceMetricsCampaignsClaimedTodayOverview(
        subscriber,
        timezone,
        moment()
            .subtract(overviewDays - 1, 'days')
            .startOf('day')
            .toDate(),
        moment().endOf('day').toDate(),
    );

    // Claims Today Overview
    const [hoveredClaimsTodayOverviewMetricId, setHoveredClaimsTodayOverviewMetricId] = useState<string>('');

    useEffect(() => {
        if (!subscriber?._id) {
            return;
        }

        const stored = localStorage.getItem(crossReferenceSubscriberIdStorageKey(subscriber._id));

        if (stored !== null) {
            setCrossReferenceSubscriberId(stored);
            return;
        }

        if (subscribers && Array.isArray(subscribers) && subscribers.length === 2) {
            const otherSubscriber = subscribers.find(s => s._id !== params.userId);

            if (otherSubscriber?._id) {
                updateCrossReferenceSubscriberId(otherSubscriber._id);
                return;
            }
        }

        setCrossReferenceSubscriberId('none');
    }, [subscriber?._id, subscribers, params.userId, updateCrossReferenceSubscriberId]);

    // Fetch full report for download
    const metricsCampaignsOverviewFullForDownload = async (includeInactive: boolean): Promise<EnrichedCampaign[]> => {
        if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId && subscriber) {
            const query: string = `${settingsContext.routes.metrics.base}${params.userId}/campaignsOverview?${new URLSearchParams({
                includeInactive: includeInactive ? 'true' : 'false',
                ...(searchDateRange[0] && searchDateRange[1]
                    ? {
                          dateRangeStartDate: moment(searchDateRange[0]).tz(timezone, true).startOf('day').format(),
                          dateRangeEndDate: moment(searchDateRange[1]).tz(timezone, true).endOf('day').format(),
                      }
                    : {}),
                startDate: moment(claimedTodayDate).tz(timezone, true).startOf('day').format(),
                endDate: moment(claimedTodayDate).tz(timezone, true).endOf('day').format(),
                search,
                sort: reportSort && reportSort.length > 0 ? `${reportSort[0].field}=${reportSort[0].sort}` : '',
            })}`;

            const data = await axios
                .get(query, { timeout: 1200000 })
                .then(response => response.data as EnrichedCampaign[])
                .catch(error => {
                    console.error(error);
                    handleHttpError(error, dialog);

                    return [];
                });

            return data;
        }

        return [];
    };

    const handleCancelExport = () => {
        exportCancelledRef.current = true;

        if (exportAbortControllerRef.current) {
            exportAbortControllerRef.current.abort();
            exportAbortControllerRef.current = null;
        }

        setExportDialogOpen(false);
        setCampaignsFullForDownloadLoading(false);
    };

    const enrichCampaignsWithCrossReferenceEarnings = async (campaigns: EnrichedCampaign[]): Promise<EnrichedCampaign[] | null> => {
        if (crossReferenceSubscriberId === 'none' || campaigns.length === 0) {
            return campaigns;
        }

        exportAbortControllerRef.current = new AbortController();
        const signal = exportAbortControllerRef.current.signal;

        setExportProgress({
            message: `Fetching cross-reference earnings: 0 of ${campaigns.length.toLocaleString()}`,
            fetched: 0,
            total: campaigns.length,
        });

        const enrichedCampaigns: EnrichedCampaign[] = [];
        const fetchDeps = {
            axios,
            metricsBase: settingsContext.routes.metrics.base,
            userId: params.userId as string,
            dialog,
        };

        for (let i = 0; i < campaigns.length; i++) {
            if (signal.aborted || exportCancelledRef.current) {
                return null;
            }

            const campaign = campaigns[i];

            try {
                const crossReferenceEarnings = await queryClient.fetchQuery(
                    ['metricsCrossReferenceMetricEarnings', userContext.jwtToken, params.userId, campaign._id, crossReferenceSubscriberId],
                    () => fetchMetricCrossReferenceEarnings(fetchDeps, campaign._id, 'campaign', crossReferenceSubscriberId, signal),
                    { staleTime: 1000 * 60 * 5 },
                );

                enrichedCampaigns.push({
                    ...campaign,
                    crossReferenceEarnings: crossReferenceEarnings ?? undefined,
                });
            } catch (error: any) {
                if (error.name === 'CanceledError' || error.name === 'AbortError') {
                    return null;
                }

                console.error(`Error fetching cross-reference earnings for campaign ${campaign._id}:`, error);
                return null;
            }

            const fetched = i + 1;
            setExportProgress({
                message: `Fetching cross-reference earnings: ${fetched.toLocaleString()} of ${campaigns.length.toLocaleString()}`,
                fetched,
                total: campaigns.length,
            });

            if (fetched < campaigns.length && !signal.aborted) {
                await delay(100);
            }
        }

        exportAbortControllerRef.current = null;

        return enrichedCampaigns;
    };

    const handleExportReport = async (format: 'csv' | 'excel') => {
        exportCancelledRef.current = false;
        setCampaignsFullForDownloadLoading(true);
        setExportProgress({ message: 'Fetching report...', fetched: 0, total: 0 });
        setExportDialogOpen(true);

        try {
            const data = await queryClient.fetchQuery(
                ['metricsCampaignsOverviewFullForDownload', timezone, claimedTodayDate, trialsSelectorType, subscriber, search],
                () => metricsCampaignsOverviewFullForDownload(trialsSelectorType.includeInactive),
                { staleTime: 60 * 1000 * 5 },
            );

            if (exportCancelledRef.current) {
                return;
            }

            const enrichedData = await enrichCampaignsWithCrossReferenceEarnings(data);

            if (enrichedData === null || exportCancelledRef.current) {
                return;
            }

            setExportProgress({ message: 'Generating export...', fetched: 0, total: 0 });

            if (format === 'csv') {
                handleDownloadReportCsv(enrichedData);
            } else {
                handleDownloadReportExcel(enrichedData);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setCampaignsFullForDownloadLoading(false);
            setExportDialogOpen(false);
            exportAbortControllerRef.current = null;
            exportCancelledRef.current = false;
        }
    };

    // *** SETTINGS ***
    const saveSettings = (
        campaignId: string,
        platform: string,
        associatedUsername: string,
        goalSpent: number,
        goalSubs: number,
        addUsersToList: { id: number | string | OnlyFansLists.Type; name: string } | null | undefined,
        callBack: () => void,
    ) => {
        const doUpdate = async () => {
            if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId && subscriber) {
                const query: string = `${settingsContext.routes.metrics.base}${params.userId}/promocampaigns/${campaignId}`;

                setIsSavingSettings(true);

                const body: any = {
                    ...(platform !== '' && { platform }),
                    ...(associatedUsername !== '' && { associatedUsername }),
                    ...(goalSpent !== 0 && { goalSpent }),
                    ...(goalSubs !== 0 && { goalSubs }),
                    addUsersToList,
                };

                // Update campaign settings
                const updatedMetric = await axios
                    .put(query, body)
                    .then(response => response.data)
                    .catch(error => {
                        console.error(error);
                        setIsSavingSettings(false);
                        handleHttpError(error, dialog);
                    });

                if (updatedMetric) {
                    campaignsRefetch();
                }

                setIsSavingSettings(false);
                callBack();
            }
        };

        doUpdate();
    };

    const saveSetMetricCompleted = (metricId: string, completed: boolean, callBack: () => void) => {
        const doUpdate = async () => {
            if (userContext.jwtToken && settingsContext.apiKey && 'userId' in params && params.userId && subscriber) {
                const query: string = `${settingsContext.routes.metrics.base}${params.userId}/promocampaigns/${metricId}/completed`;

                setIsSavingSettings(true);

                const body: any = {
                    completed,
                };

                // Update campaign settings
                const updatedMetric = await axios
                    .put(query, body)
                    .then(response => response.data)
                    .catch(error => {
                        console.error(error);
                        setIsSavingSettings(false);
                        handleHttpError(error, dialog);
                    });

                if (updatedMetric) {
                    campaignsRefetch();
                }

                setIsSavingSettings(false);
                callBack();
            }
        };

        doUpdate();
    };

    // Convert report to CSV format and start file download
    const handleDownloadReportCsv = (data: EnrichedCampaign[]) => {
        if (!data) {
            return;
        }

        const transformedData: any = transformTransactionsForExport(data, amountType, timezone);

        // Format JSON data to CSV
        const csv = parseCsv(transformedData.rows, {
            fields: transformedData.headers,
            transforms: [],
        });

        // Convert CSV to Blob
        const blob: Blob = new Blob([csv], { type: 'text/csv' });

        // Create a descriptive filename
        const filename: string = `campaigns_${moment().tz(timezone, true).format('YYYY-MM-DD')}.csv`;

        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode && link.parentNode.removeChild(link);
    };

    // Convert to Excel format and start file download
    const handleDownloadReportExcel = (data: EnrichedCampaign[]) => {
        if (!data) {
            return;
        }

        const transformedData: any = transformTransactionsForExport(data, amountType, timezone);

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet([]);

        XLSX.utils.sheet_add_aoa(worksheet, [transformedData.headers.map((header: any) => header.label)]);

        XLSX.utils.sheet_add_json(worksheet, transformedData.rows, {
            origin: 'A2',
            skipHeader: true,
            header: transformedData.headers.map((header: any) => header.value),
        });

        XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');

        // Create a descriptive filename
        const filename: string = `campaigns_${moment(claimedTodayDate).tz(timezone, true).format('YYYY-MM-DD')}.xlsx`;

        XLSX.writeFile(workbook, filename);
    };

    return (
        <>
            <Card sx={{ width: '100%', marginBottom: 1 }}>
                <CardContent sx={{ padding: 2, '&:last-child': { paddingBottom: 2 } }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} lg>
                            <OverviewStat
                                icon={<LinkIcon htmlColor={green[800]} />}
                                iconBackgroundColor={green[100]}
                                label={`Total Earnings (${amountType === 'gross' ? 'gross' : 'net'})`}
                                value={dinero({
                                    amount: Math.trunc(earningsTotal * 100 * (amountType === 'gross' ? 1 : 0.8)),
                                    currency: 'USD',
                                }).toFormat()}
                                valueColor={green[800]}
                                loading={campaignslsLoading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg>
                            <OverviewStat
                                icon={<PeopleAltIcon htmlColor={blue[700]} />}
                                iconBackgroundColor={blue[50]}
                                label="Total Subscribers"
                                value={subscribersTotal.toLocaleString()}
                                loading={campaignslsLoading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg>
                            <OverviewStat
                                icon={<AdsClickIcon htmlColor={orange[800]} />}
                                iconBackgroundColor={orange[50]}
                                label="Total Clicks"
                                value={clicksTotal.toLocaleString()}
                                loading={campaignslsLoading}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg>
                            <OverviewStat
                                icon={<ShowChartIcon htmlColor={purple[700]} />}
                                iconBackgroundColor={purple[50]}
                                label="Click to Fan Conversion"
                                value={`${(subscribersTotal && clicksTotal ? (subscribersTotal / clicksTotal) * 100 : 0).toFixed(2)}%`}
                                loading={campaignslsLoading}
                            />
                        </Grid>
                        <Grid item xs={12} lg="auto">
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <Tooltip title="Instructions">
                                    <IconButton size="small" aria-label="Instructions" onClick={() => setShowInstructions(true)}>
                                        <HelpOutlineIcon fontSize="small" color="primary" />
                                    </IconButton>
                                </Tooltip>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    fullWidth={!isLargeScreen}
                                    component={Link}
                                    to={`/subscribers/${'userId' in params && params.userId}/services/sextforce/metrics/trials/overview`}
                                >
                                    Switch to Free Trial Links
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <DialogAdaptive
                open={showInstructions}
                onClose={() => setShowInstructions(false)}
                maxWidth="sm"
                fullWidth
                scroll="paper"
                aria-labelledby="tracking-links-instructions-dialog-title"
            >
                <StyledCardTitleBar title="Tracking Links" />
                <DialogContent sx={{ p: 1 }}>
                    <ul style={{ paddingLeft: theme.spacing(2), marginBottom: 1, marginTop: 1 }}>
                        <li>
                            Select the date for which to show that day's gains. The total amount earned won't change. It is showing the
                            latest total for the entire link.
                        </li>
                        <li>
                            Select another account with an active Sextforce subscription to see how much you've earned from the fans who
                            came through each campaign on another account from the time they came through that campaign until now.
                        </li>
                        <li>Click on the CSV and Excel icons to download the report.</li>
                        <li>Use the search field to filter through the results.</li>
                        <li>Click on the Edit icon in the results to change the settings for that tracking link.</li>
                        <li>Click on the Share icon in the results to send automatic updates to your colleagues.</li>
                    </ul>
                </DialogContent>
                <StyledDialogActions>
                    <Button onClick={() => setShowInstructions(false)}>Close</Button>
                </StyledDialogActions>
            </DialogAdaptive>

            <SextforceMetricsCampaignsClaimsTodayOverview
                subscriber={subscriber}
                hoveredMetricId={hoveredClaimsTodayOverviewMetricId}
                setHoveredMetricId={setHoveredClaimsTodayOverviewMetricId}
                overviewDays={overviewDays}
                setOverviewDays={setOverviewDays}
                claimsTodayOverview={claimsTodayOverview}
                claimsTodayOverviewLoading={claimsTodayOverviewLoading}
            />

            <Card sx={{ width: '100%', marginBottom: 1 }}>
                <CardContent sx={{ padding: 2 }}>
                    <Grid
                        container
                        flexGrow={1}
                        spacing={1}
                        alignItems="center"
                        justifyContent={isLargeScreen ? 'flex-start' : 'space-between'}
                    >
                        <Grid item xs={12}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs>
                                    <Typography variant="h6">Metrics</Typography>
                                </Grid>
                                <Grid item xs="auto">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Tooltip title="Download as CSV">
                                            <span>
                                                <IconButton
                                                    disabled={campaingsFullForDownloadLoading || !campaigns}
                                                    onClick={() => handleExportReport('csv')}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faFileCsv}
                                                        size="2x"
                                                        color={
                                                            campaingsFullForDownloadLoading
                                                                ? theme.palette.text.disabled
                                                                : theme.palette.info.main
                                                        }
                                                    />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Download as Excel">
                                            <span>
                                                <IconButton
                                                    disabled={campaingsFullForDownloadLoading || !campaigns}
                                                    onClick={() => handleExportReport('excel')}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faFileExcel}
                                                        size="2x"
                                                        color={
                                                            campaingsFullForDownloadLoading
                                                                ? theme.palette.text.disabled
                                                                : theme.palette.info.main
                                                        }
                                                    />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} sm="auto">
                            <Button
                                variant="outlined"
                                color="primary"
                                size="large"
                                fullWidth
                                onClick={() => setSettingsDialogOpen(true)}
                                sx={{ marginTop: '4px', height: '39px' }}
                            >
                                View Settings
                            </Button>
                            {settingsDialogOpen && (
                                <SextforceMetricsCampaignsOverviewViewSettingsDialog
                                    open={settingsDialogOpen}
                                    onClose={() => setSettingsDialogOpen(false)}
                                    timezone={timezone}
                                    setTimezone={setTimezone}
                                    campaignsLoading={campaignslsLoading}
                                    campaignsSelectorType={trialsSelectorType}
                                    setCampaignsSelectorType={setTrialsSelectorType}
                                    completedType={completedType}
                                    setCompletedType={setCompletedType}
                                    subscribers={subscribers}
                                    subscribersLoading={subscribersLoading}
                                    crossReferenceSubscriberId={crossReferenceSubscriberId}
                                    setCrossReferenceSubscriberId={updateCrossReferenceSubscriberId}
                                    amountType={amountType}
                                    setAmountType={setAmountType}
                                />
                            )}
                        </Grid>

                        <Grid item xs={12} sm="auto">
                            <Button
                                onClick={() => {
                                    setCreateCampaignDialogOpen(true);
                                }}
                                variant="contained"
                                color="secondary"
                                size="medium"
                                fullWidth
                                startIcon={
                                    <AddIcon
                                        sx={{
                                            animation: 'spin-pause 10s linear infinite',
                                            animationTimingFunction: 'linear',
                                            '@keyframes spin-pause': {
                                                '0%': { transform: 'rotate(0deg)' },
                                                '4%': { transform: 'rotate(360deg)' },
                                                '100%': { transform: 'rotate(360deg)' },
                                            },
                                        }}
                                    />
                                }
                                sx={{ marginTop: '4px', height: '39px' }}
                            >
                                Create Tracking Link
                            </Button>
                        </Grid>
                    </Grid>

                    <Box sx={{ marginTop: 2 }}>
                        <SextforceMetricsCampaignsOverviewGrid
                            subscriber={subscriber}
                            metrics={campaigns}
                            amountType={amountType}
                            isLoading={campaignslsLoading}
                            saveSetMetricCompleted={saveSetMetricCompleted}
                            saveSettings={saveSettings}
                            isSavingSettings={isSavingSettings}
                            reportSort={reportSort}
                            setReportSort={setReportSort}
                            // filter={filter}
                            // setFilter={setFilter}
                            search={search}
                            setSearch={setSearch}
                            searchDateRange={searchDateRange}
                            setSearchDateRange={setSearchDateRange}
                            paginationModel={paginationModel}
                            setPaginationModel={setPaginationModel}
                            crossReferenceSubscriberId={crossReferenceSubscriberId}
                            startDate={moment(claimedTodayDate).startOf('day').toDate()}
                            endDate={moment(claimedTodayDate).endOf('day').toDate()}
                            claimedTodayDate={claimedTodayDate}
                            setClaimedTodayDate={setClaimedTodayDate}
                            timezone={timezone}
                            setTimezone={setTimezone}
                            hoverMetricId={hoveredClaimsTodayOverviewMetricId}
                            claimsTodayOverview={claimsTodayOverview}
                            claimsTodayOverviewLoading={claimsTodayOverviewLoading}
                        />
                    </Box>
                </CardContent>
            </Card>

            <SextforceMetricsCampaignsCreateCampaignDialog
                open={createCampaignDialogOpen}
                onClose={() => setCreateCampaignDialogOpen(false)}
                campaignsRefetch={campaignsRefetch}
            />

            <SextforceMetricsCampaignsOverviewExportProgressDialog
                open={exportDialogOpen}
                message={exportProgress.message}
                fetched={exportProgress.fetched}
                total={exportProgress.total}
                onCancel={handleCancelExport}
            />
        </>
    );
};

export default SextforceMetricsCampaignsOverview;
